import "@shopify/ui-extensions/preact";
import { render } from "preact";
import { useEffect, useState } from "preact/hooks";
import { BACKEND_URL_FROM_ENV } from "./backend-url.generated";

const EXTENSION_SOURCE = "sample-free-gift-checkout-sync";
const EXTENSION_TARGET = "purchase.checkout.cart-line-list.render-after";
const SAMPLE_ATTRIBUTE_KEY = "_SampleFreeGift";
const SAMPLE_ATTRIBUTE_VALUE = "true";
const SYNC_RETRY_DELAY_MS = 350;
const CONFIG_RETRY_DELAYS_MS = [500, 1500, 4000, 10000];
const MAX_BLOCK_MS = 15000;
const SYNC_PENDING_MESSAGE =
  "Updating your free sample. Please wait a moment and try again.";

function getCheckoutApi(args) {
  const candidates = [...args, globalThis.shopify].filter(Boolean);

  return candidates.find(
    (candidate) =>
      typeof candidate.applyCartLinesChange === "function" &&
      candidate.lines &&
      candidate.shop,
  );
}

function getSignalValue(signal, fallback = undefined) {
  return signal?.value ?? signal?.current ?? fallback;
}

function getShopDomain(api) {
  return api?.shop?.myshopifyDomain || "";
}

/**
 * Draft-order checkouts (and some other checkout types) fix the line items, so
 * Shopify rejects `applyCartLinesChange` with "not supported on this checkout".
 * `instructions.lines` says up front which change types are allowed. When the
 * instructions are unavailable the change is assumed allowed, as before.
 */
const LINE_CHANGE_PERMISSION = {
  addCartLine: "canAddCartLine",
  removeCartLine: "canRemoveCartLine",
  updateCartLine: "canUpdateCartLine",
};

// Set once Shopify refuses a line change as unsupported, so this checkout is
// never retried (and never held) again.
let lineChangesLocked = false;

function isDraftOrderCheckout(api) {
  return (
    getSignalValue(api?.checkoutSettings, null)?.orderSubmission ===
    "DRAFT_ORDER"
  );
}

function isUnsupportedLineChangeError(message) {
  return /not supported on this checkout/i.test(String(message ?? ""));
}

function canApplyLineChange(api, type) {
  // Draft order checkouts never accept line changes, even when `instructions`
  // does not say so; their gift lines are synced on the Draft Order by the app.
  if (lineChangesLocked || isDraftOrderCheckout(api)) return false;

  const lines = getSignalValue(api?.instructions, null)?.lines;
  const permission = LINE_CHANGE_PERMISSION[type];

  if (!lines || !permission || typeof lines[permission] !== "boolean") {
    return true;
  }

  return lines[permission];
}

function filterSupportedLineChanges(api, changes) {
  return changes.filter((change) => canApplyLineChange(api, change.type));
}

function getCheckoutToken(api) {
  return getSignalValue(api?.checkoutToken, null) || null;
}

function normalizeAttributes(attributes) {
  return Array.isArray(attributes)
    ? attributes.flatMap((attribute) =>
        attribute?.key
          ? [{ key: String(attribute.key), value: String(attribute.value ?? "") }]
          : [],
      )
    : [];
}

function createSampleAttribute() {
  return { key: SAMPLE_ATTRIBUTE_KEY, value: SAMPLE_ATTRIBUTE_VALUE };
}

/** A line this extension added as a sample gift. */
function isSampleGiftLine(line) {
  return normalizeAttributes(line?.attributes).some(
    (attribute) =>
      attribute.key === SAMPLE_ATTRIBUTE_KEY &&
      attribute.value.toLowerCase() === SAMPLE_ATTRIBUTE_VALUE,
  );
}

function withSampleAttribute(attributes) {
  const next = normalizeAttributes(attributes).filter(
    (attribute) => attribute.key !== SAMPLE_ATTRIBUTE_KEY,
  );

  next.push(createSampleAttribute());

  return next;
}

function getCartLineSignature(cartLines) {
  return cartLines
    .map((line) => {
      const attributes = normalizeAttributes(line.attributes)
        .map((attribute) => `${attribute.key}:${attribute.value}`)
        .sort()
        .join("|");

      return `${line.id}:${line.merchandise?.id}:${line.quantity}:${attributes}`;
    })
    .join(",");
}

// ---------------------------------------------------------------- logging

const MAX_LOG_BODY_BYTES = 60000;

function serializeLogBody(body) {
  const text = JSON.stringify(body);

  if (text.length <= MAX_LOG_BODY_BYTES) return text;

  return JSON.stringify({
    shop: body.shop,
    source: body.source,
    extensionTarget: body.extensionTarget,
    level: body.level,
    eventType: body.eventType,
    message: body.message,
    checkoutToken: body.checkoutToken,
    payload: { payloadTruncated: true },
  });
}

async function logEvent(api, level, eventType, message, payload = {}) {
  const backendUrl = String(BACKEND_URL_FROM_ENV || "")
    .trim()
    .replace(/\/$/, "");
  const shop = getShopDomain(api);

  if (!backendUrl || !shop) return;

  try {
    const cartLines = getSignalValue(api?.lines, []) ?? [];
    const response = await fetch(`${backendUrl}/api/app-event-log`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // `keepalive` so the entry survives checkout navigating away.
      keepalive: true,
      body: serializeLogBody({
        shop,
        source: EXTENSION_SOURCE,
        extensionTarget: EXTENSION_TARGET,
        level,
        eventType,
        message,
        checkoutToken: getCheckoutToken(api),
        payload: {
          ...payload,
          checkoutData: {
            lineCount: cartLines.length,
            sampleGiftLineCount: cartLines.filter(isSampleGiftLine).length,
            lines: cartLines.map((line) => ({
              id: line?.id ?? null,
              variantId: line?.merchandise?.id ?? null,
              title: line?.merchandise?.title ?? null,
              quantity: line?.quantity ?? null,
              isSampleGift: isSampleGiftLine(line),
            })),
          },
        },
      }),
    });

    if (!response.ok) {
      console.error("Sample free gift log rejected", response.status);
    }
  } catch (error) {
    console.error("Sample free gift log failed", error);
  }
}

// ------------------------------------------------------------------ config

async function fetchSampleFreeGiftRules(api) {
  const backendUrl = String(BACKEND_URL_FROM_ENV || "")
    .trim()
    .replace(/\/$/, "");
  const shop = getShopDomain(api);

  if (!backendUrl || !shop) return [];

  const response = await fetch(
    `${backendUrl}/api/sample-free-gift-config?shop=${encodeURIComponent(shop)}`,
    { cache: "no-store" },
  );

  if (!response.ok) {
    // Throwing lets the caller retry rather than silently running with no rules.
    throw new Error(`Sample free gift config responded with ${response.status}.`);
  }

  const json = await response.json();

  return (Array.isArray(json?.rules) ? json.rules : []).flatMap((rule) => {
    const productXVariantIds = (rule?.productXVariantIds ?? []).filter(Boolean);
    const sampleVariantIds = (rule?.sampleVariantIds ?? []).filter(Boolean);

    if (!productXVariantIds.length || !sampleVariantIds.length) return [];

    const productYQuantity = Number(rule?.productYQuantity);

    return [
      {
        productXVariantIds,
        sampleVariantIds,
        excludeVariantIds: (rule?.excludeVariantIds ?? []).filter(Boolean),
        productYQuantity:
          Number.isFinite(productYQuantity) && productYQuantity > 0
            ? Math.trunc(productYQuantity)
            : 1,
      },
    ];
  });
}

// ------------------------------------------------------------------- rules

// A matching rule grants exactly the quantity configured on it, however many
// Product X items are in the cart.
const DEFAULT_PRODUCT_Y_QUANTITY = 1;

function getProductYQuantity(rule) {
  const quantity = Number(rule?.productYQuantity);

  return Number.isFinite(quantity) && quantity > 0
    ? Math.trunc(quantity)
    : DEFAULT_PRODUCT_Y_QUANTITY;
}

/**
 * Evaluates one rule against the cart.
 *
 * The sample line itself never counts as Product X or as an exclusion, so the
 * rule cannot react to the gift it just added.
 */
export function evaluateRule(cartLines, rule) {
  const buyerLines = cartLines.filter((line) => !isSampleGiftLine(line));
  const excludeSet = new Set(rule.excludeVariantIds);
  const productXSet = new Set(rule.productXVariantIds);

  const isExcluded = buyerLines.some((line) =>
    excludeSet.has(line?.merchandise?.id),
  );

  const productXQuantity = buyerLines.reduce((total, line) => {
    if (!productXSet.has(line?.merchandise?.id)) return total;

    const quantity = Number(line?.quantity);

    return total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0);
  }, 0);

  const matched = !isExcluded && productXQuantity > 0;

  return {
    matched,
    isExcluded,
    productXQuantity,
    // Rule 2: however many Product X variants match, only ONE sample variant
    // is granted - the first configured one.
    sampleVariantId: rule.sampleVariantIds[0],
    desiredQuantity: matched ? getProductYQuantity(rule) : 0,
  };
}

/**
 * Produces the cart changes needed to make the cart match the rules.
 *
 * Rule TRUE  + correct quantity   -> no change
 * Rule TRUE  + incorrect quantity -> update
 * Rule TRUE  + not in cart        -> add
 * Rule FALSE + in cart            -> remove
 * Rule FALSE + not in cart        -> no action
 */
export function buildSampleGiftChanges(cartLines, rules, evaluations = null) {
  const lines = Array.isArray(cartLines) ? cartLines : [];
  const desiredByVariant = {};
  const managedVariantIds = new Set();

  rules.forEach((rule, index) => {
    for (const variantId of rule.sampleVariantIds) {
      managedVariantIds.add(variantId);
    }

    // Reuse the caller's evaluation when given, so each rule is checked once.
    const { desiredQuantity, sampleVariantId } =
      evaluations?.[index] ?? evaluateRule(lines, rule);

    // Never accumulated: when several matching rules grant the same sample,
    // the largest configured quantity wins.
    if (desiredQuantity > 0) {
      desiredByVariant[sampleVariantId] = Math.max(
        desiredByVariant[sampleVariantId] ?? 0,
        desiredQuantity,
      );
    }
  });

  const sampleLinesByVariant = {};
  const buyerLinesByVariant = {};

  for (const line of lines) {
    const variantId = line?.merchandise?.id;

    if (!variantId || !managedVariantIds.has(variantId)) continue;

    const bucket = isSampleGiftLine(line)
      ? sampleLinesByVariant
      : buyerLinesByVariant;

    bucket[variantId] ??= [];
    bucket[variantId].push({
      id: line.id,
      quantity: line.quantity,
      attributes: line.attributes ?? [],
    });
  }

  const changes = [];

  for (const variantId of managedVariantIds) {
    const desiredQuantity = desiredByVariant[variantId] ?? 0;
    const sampleLines = sampleLinesByVariant[variantId] ?? [];
    const buyerLines = buyerLinesByVariant[variantId] ?? [];

    // Rule FALSE: remove the sample we added. A line the buyer added
    // themselves is left alone.
    if (desiredQuantity <= 0) {
      for (const line of sampleLines) {
        changes.push({
          type: "removeCartLine",
          id: line.id,
          quantity: line.quantity,
        });
      }
      continue;
    }

    if (sampleLines.length === 0) {
      const [primaryBuyerLine, ...extraBuyerLines] = buyerLines;

      // Rule 4: never create a second line for a variant already in the cart.
      if (primaryBuyerLine) {
        changes.push({
          type: "updateCartLine",
          id: primaryBuyerLine.id,
          quantity: desiredQuantity,
          attributes: withSampleAttribute(primaryBuyerLine.attributes),
        });

        for (const line of extraBuyerLines) {
          changes.push({
            type: "removeCartLine",
            id: line.id,
            quantity: line.quantity,
          });
        }

        continue;
      }

      changes.push({
        type: "addCartLine",
        merchandiseId: variantId,
        quantity: desiredQuantity,
        attributes: [createSampleAttribute()],
      });
      continue;
    }

    // A sample line exists: collapse any duplicate of the same variant.
    for (const line of buyerLines) {
      changes.push({
        type: "removeCartLine",
        id: line.id,
        quantity: line.quantity,
      });
    }

    const [primarySampleLine, ...extraSampleLines] = sampleLines;

    if (primarySampleLine.quantity !== desiredQuantity) {
      changes.push({
        type: "updateCartLine",
        id: primarySampleLine.id,
        quantity: desiredQuantity,
        attributes: withSampleAttribute(primarySampleLine.attributes),
      });
    }

    for (const line of extraSampleLines) {
      changes.push({
        type: "removeCartLine",
        id: line.id,
        quantity: line.quantity,
      });
    }
  }

  return changes;
}

// ------------------------------------------------------------------- render

let bannerState = { message: "" };
const bannerListeners = new Set();

function setBannerMessage(message) {
  if (bannerState.message === message) return;

  bannerState = { message };
  bannerListeners.forEach((listener) => listener(bannerState));
}

function SampleGiftBanner() {
  const [state, setState] = useState(bannerState);

  useEffect(() => {
    setState(bannerState);
    bannerListeners.add(setState);

    return () => {
      bannerListeners.delete(setState);
    };
  }, []);

  if (!state.message) return null;

  return <s-banner tone="critical">{state.message}</s-banner>;
}

// -------------------------------------------------------------------- main

export default function (...args) {
  const api = getCheckoutApi(args);

  if (!api) {
    console.error("Sample free gift extension could not access Checkout API.");
    return;
  }

  let rules = [];
  let configLoaded = false;
  let isSyncing = false;
  let cartNeedsSync = false;
  let syncInFlight = false;
  let resyncQueued = false;
  let lineChangesUnsupportedLogged = false;
  let lastSignature = "";
  let firstBlockedAt = null;
  let syncTimer;
  let retryTimer;

  const scheduleSync = (delay = 0) => {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      void syncSampleGiftLines();
    }, delay);
  };

  async function syncSampleGiftLines() {
    if (!configLoaded) return;

    if (syncInFlight) {
      resyncQueued = true;
      return;
    }

    syncInFlight = true;

    try {
      await runSampleGiftSync();
    } finally {
      syncInFlight = false;

      if (resyncQueued) {
        resyncQueued = false;
        scheduleSync();
      }
    }
  }

  async function runSampleGiftSync() {
    const cartLines = getSignalValue(api.lines, []) ?? [];
    const signature = `${JSON.stringify(rules)}::${getCartLineSignature(cartLines)}`;

    if (signature === lastSignature && !cartNeedsSync) return;
    lastSignature = signature;

    // Each rule is evaluated exactly once per sync and reused below.
    const evaluations = rules.map((rule) => evaluateRule(cartLines, rule));
    const plannedChanges = buildSampleGiftChanges(cartLines, rules, evaluations);
    // Only attempt what this checkout accepts; on a draft-order checkout line
    // changes are refused, so nothing is attempted and checkout is not held.
    const changes = filterSupportedLineChanges(api, plannedChanges);

    if (changes.length < plannedChanges.length && !lineChangesUnsupportedLogged) {
      lineChangesUnsupportedLogged = true;
      void logEvent(
        api,
        "warning",
        "sample_free_gift_line_changes_unsupported",
        "This checkout does not allow line item changes (e.g. a draft order checkout); sample gifts are skipped.",
        {
          skippedChanges: plannedChanges
            .filter((change) => !canApplyLineChange(api, change.type))
            .map((change) => change.type),
          instructions: getSignalValue(api?.instructions, null)?.lines ?? null,
          extensionTarget: EXTENSION_TARGET,
        },
      );
    }

    // Logged only when the cart has to change: an unchanged evaluation is the
    // steady state and would otherwise cost a network request each time.
    if (changes.length) {
      void logEvent(
        api,
        "info",
        "sample_free_gift_evaluated",
        "Sample free gift rules were evaluated against the cart.",
        {
          ruleCount: rules.length,
          evaluations,
          plannedChanges: changes.map((change) => change.type),
          extensionTarget: EXTENSION_TARGET,
        },
      );
    }

    if (!changes.length) {
      setBannerMessage("");
      isSyncing = false;
      cartNeedsSync = false;
      return;
    }

    isSyncing = true;
    cartNeedsSync = true;

    let failed = false;

    try {
      for (const change of changes) {
        const result = await api.applyCartLinesChange(change);

        if (result?.type === "error") {
          if (isUnsupportedLineChangeError(result.message)) {
            // This checkout refuses line changes: stop, and never hold checkout.
            lineChangesLocked = true;
            setBannerMessage("");
            void logEvent(
              api,
              "warning",
              "sample_free_gift_line_changes_unsupported",
              result.message,
              { change, extensionTarget: EXTENSION_TARGET },
            );
            break;
          }

          failed = true;
          console.error("Sample free gift sync failed", result.message);
          void logEvent(
            api,
            "error",
            "sample_free_gift_sync_error",
            result.message || "Cart line change failed.",
            { change, extensionTarget: EXTENSION_TARGET },
          );
          break;
        }

        void logEvent(
          api,
          "info",
          change.type === "addCartLine"
            ? "sample_free_gift_added"
            : change.type === "removeCartLine"
              ? "sample_free_gift_removed"
              : "sample_free_gift_qty_updated",
          "Sample free gift line was synchronised.",
          { change, extensionTarget: EXTENSION_TARGET },
        );
      }
    } finally {
      isSyncing = false;
      cartNeedsSync = failed;
      clearTimeout(retryTimer);
      retryTimer = setTimeout(() => scheduleSync(), SYNC_RETRY_DELAY_MS);
    }
  }

  api.lines?.subscribe?.(() => scheduleSync());

  // Keeps an order from completing while the sample is still being applied,
  // without ever stranding the buyer if the backend is unreachable.
  void api.buyerJourney?.intercept?.(({ canBlockProgress }) => {
    if (!canBlockProgress) return { behavior: "allow" };

    const cartLines = getSignalValue(api.lines, []) ?? [];
    const pendingChanges = configLoaded
      ? filterSupportedLineChanges(api, buildSampleGiftChanges(cartLines, rules))
      : [];
    const notReady =
      !configLoaded || isSyncing || cartNeedsSync || pendingChanges.length > 0;

    if (!notReady) {
      firstBlockedAt = null;

      return { behavior: "allow", perform: () => setBannerMessage("") };
    }

    scheduleSync();
    firstBlockedAt ??= Date.now();

    if (Date.now() - firstBlockedAt > MAX_BLOCK_MS) {
      void logEvent(
        api,
        "error",
        "sample_free_gift_block_timeout",
        "Allowing checkout without resolving sample free gifts after timeout.",
        { configLoaded, pendingChanges, extensionTarget: EXTENSION_TARGET },
      );

      return { behavior: "allow", perform: () => setBannerMessage("") };
    }

    return {
      behavior: "block",
      reason: "sample_free_gift_sync_in_progress",
      // Rendered by this extension so it clears as soon as the cart is in
      // sync, instead of lingering in checkout's own error area.
      perform: (result) => {
        setBannerMessage(
          result.behavior === "block" ? SYNC_PENDING_MESSAGE : "",
        );
      },
    };
  });

  let configAttempt = 0;

  function loadSampleFreeGiftRules() {
    void fetchSampleFreeGiftRules(api)
      .then((nextRules) => {
        configAttempt = 0;
        rules = nextRules;
        configLoaded = true;
        scheduleSync();
      })
      .catch((error) => {
        console.error("Failed to fetch sample free gift config", error);
        void logEvent(
          api,
          "error",
          "sample_free_gift_config_failed",
          error instanceof Error ? error.message : "Config load failed.",
          { attempt: configAttempt + 1, extensionTarget: EXTENSION_TARGET },
        );

        const delay =
          CONFIG_RETRY_DELAYS_MS[
            Math.min(configAttempt, CONFIG_RETRY_DELAYS_MS.length - 1)
          ];

        configAttempt += 1;
        setTimeout(loadSampleFreeGiftRules, delay);
      });
  }

  loadSampleFreeGiftRules();

  render(<SampleGiftBanner />, document.body);
}
